/**
 * How lead stats definitions name things in GoHighLevel (docs/gohighlevel-stats.spec.md,
 * "Numbers"): stages, pipelines, tags, sources and calendars are chosen by the
 * names GoHighLevel shows, so one definition works across every sub-account made
 * from the same template. Names are compared without regard to case or to the
 * spaces around and inside them, because people type them and GoHighLevel lowers
 * tags on its own.
 */

/** A GoHighLevel name as definitions compare it. */
export function normalizeGhlName(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLowerCase()
}

/** The set a list of names matches, for repeated lookups. */
export function nameSet(names: readonly string[]): Set<string> {
  return new Set(names.map(normalizeGhlName))
}

/**
 * The names in `wanted` that `available` doesn't have, as they were written in
 * `wanted`: what the settings page points out as no longer found.
 */
export function missingNames(
  wanted: readonly string[],
  available: readonly string[],
): string[] {
  const have = nameSet(available)
  return wanted.filter((name) => !have.has(normalizeGhlName(name)))
}

/**
 * Names without repeats, first spelling kept: what a definition stores when someone
 * picks "Booked" and types "booked " too.
 */
export function distinctNames(names: readonly string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const name of names) {
    const key = normalizeGhlName(name)
    if (key === '' || seen.has(key)) continue
    seen.add(key)
    out.push(name.trim())
  }
  return out
}
